import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import type { UseProductOffersModalsArgs, UseProductOffersModalsReturn } from './useProductOffersModals.types';

/**
 * Modal-dispatch controller for ProductOffersPage: owns which of the page's
 * modal surfaces (CheckAvailabilityDialog, LeadCaptureDialog, cart drawer)
 * shows, and the qualification/lead-capture status transitions between them.
 */
export const useProductOffersModals = ({
  sessionId,
  cart,
  isPendingWorkOrder,
  onClose,
}: UseProductOffersModalsArgs): UseProductOffersModalsReturn => {
  const analytics = useAnalytics();
  const { resolvedAddressId, qualifiedAddressId, qualificationResult, isChangeAddressOpen, leadCaptureStatus, setQualificationFlow } =
    useQualificationFlowStore(
      useShallow((s) => ({
        resolvedAddressId: s.resolvedAddressId,
        qualifiedAddressId: selectQualifiedAddressId(s),
        qualificationResult: s.qualificationResult,
        isChangeAddressOpen: s.isChangeAddressOpen,
        leadCaptureStatus: s.leadCaptureStatus,
        setQualificationFlow: s.setQualificationFlow,
      }))
    );

  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [drawerCheckoutError, setDrawerCheckoutError] = useState(false);

  const showCheckAvailability =
    qualificationResult === null || isPendingWorkOrder || !!isChangeAddressOpen || leadCaptureStatus === 'dismissed';
  const checkAvailabilityInitialView = isChangeAddressOpen ? 'change_address_warning' : undefined;

  const closeChangeAddress = () => setQualificationFlow((prev) => ({ ...prev, isChangeAddressOpen: false }));
  // The non-change-address branch must stay the page's onClose reference so
  // the dialog's (event, reason) arguments reach the MFE close path intact.
  const handleCheckAvailabilityClose = isChangeAddressOpen ? closeChangeAddress : onClose;

  const handleQualificationSuccess = (): void => {
    setQualificationFlow((prev) => ({
      ...prev,
      isChangeAddressOpen: false,
      leadCaptureStatus: undefined,
    }));
  };

  const handleNavigateToLeadCapture = () => {
    setQualificationFlow((prev) => ({ ...prev, leadCaptureStatus: 'open' }));
  };

  const handleLeadCaptureDismiss = () => {
    setQualificationFlow((prev) => ({ ...prev, leadCaptureStatus: 'dismissed' }));
  };

  const handleLeadCaptureSuccess = () => {
    setQualificationFlow((prev) => ({ ...prev, leadCaptureStatus: undefined }));
    onClose();
  };

  const handleShowCart = () => {
    setDrawerCheckoutError(false);
    setCartDrawerOpen(true);
    if (!sessionId || !cart) return;
    analytics.fire('trackViewCartInfo', {
      sessionId,
      cart,
      qualification: { qualifiedAddressId, resolvedAddressId },
    });
  };

  const handleCloseCart = () => {
    setCartDrawerOpen(false);
  };

  return {
    showCheckAvailability,
    checkAvailabilityInitialView,
    handleCheckAvailabilityClose,
    handleQualificationSuccess,
    handleNavigateToLeadCapture,
    isLeadCaptureOpen: leadCaptureStatus === 'open',
    handleLeadCaptureDismiss,
    handleLeadCaptureSuccess,
    cartDrawerOpen,
    drawerCheckoutError,
    setDrawerCheckoutError,
    handleShowCart,
    handleCloseCart,
  };
};
